import { api } from "~/utils/api"
import { useCallback, useMemo, useRef, useState } from "react"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { type AgGridReact, type CustomCellRendererProps } from "ag-grid-react"
import { type CellClickedEvent, type ColDef, type GridOptions } from "ag-grid-community"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { formatNumberWithSuffix } from "~/utils/formatting"
import { isPast } from "~/utils/datetime"
import { type AggregationMethod } from "~/components/reusable/AggregationSelector"
import { first, startCase, sumBy } from "lodash"
import { filterDefined, groupBy, uniq } from "~/utils/tsUtils"
import CustomAgGridTooltip from "~/components/reusable/table/tooltips/CustomTooltip"
import { getTailwindValue } from "~/utils/tailwind"
import { Badge } from "~/components/reusable/Badge"
import { IncomingOrderStatus } from "~/constants/incomingOrdersStatus"
import {
  filterIncomingOrders,
  type IncomingOrderDetail,
  skuCostShareInOrder,
  skuQuantityInOrder,
} from "~/utils/incomingOrdersUtils"
import IncomingOrdersDrawer from "~/components/purchaseOrder/incomingOrders/IncomingOrdersDrawer"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useFilters } from "~/hooks/useFilters"
import { filterOnSearchString } from "~/utils/search"

export type IncomingOrderRow = {
  id: string
  productionOrderId: string
  productionOrderStatusAggKey: string
  variantCount: number
  quantity: number
  cost: number
  warehouseId: string
  expectedArrivalDate: Date
  expectedDepartureDate: Date | null
  orderDate: Date
  shipmentMethod: string | null | undefined
  variantIds: string[]
  variantNames: string[]
  status: string
  source?: "Hakio" | "ERP"
  externalCollectionId?: string | null
  vendorId?: string
  extraData?: Record<string, unknown> | null
  // Additional fields for aggregation
  variantId?: string
  warehouseName?: string

  rawIncomingOrders: IncomingOrderDetail[]
}

const GroupCellRenderer: React.FC<
  CustomCellRendererProps<IncomingOrderRow> & { currency: string }
> = (props: CustomCellRendererProps<IncomingOrderRow> & { currency: string }) => {
  const { node, value, currency } = props
  const entries = filterDefined(node?.allLeafChildren?.map((n) => n.data))
  const firstEntry = first(entries)

  const quantity = node.aggData?.totalQuantity
  const cost = node.aggData?.totalCost

  if (!firstEntry) return <span>{value}</span>

  // Handle different value types to prevent React rendering errors
  let title = "Unknown"

  if (value) {
    if (Array.isArray(value)) {
      // For arrays (like dates), format them properly
      if (value.length > 0 && value[0] instanceof Date) {
        // Handle array of dates
        const formattedDates = value.map((date) => formatDate(date, DateFormatType.FullDateShort))
        title =
          formattedDates.length > 1 ? `${formattedDates.length} dates` : formattedDates[0] || ""
      } else {
        // Handle other arrays
        title = value.length > 1 ? `${value.length} items` : String(value[0] || "")
      }
    } else if (value instanceof Date) {
      // Handle single date
      title = formatDate(value, DateFormatType.FullDateShort)
    } else if (node.field === "status") {
      title = startCase(value)
    } else {
      // Handle strings and other values
      title = String(value)
    }
  }

  return (
    <div className="flex items-center gap-2 leading-5 h-[40px]">
      <span className="label-base">{title}</span>
      {quantity && node.field !== "totalQuantity" && (
        <Badge variant="neutral" size="sm">
          {formatNumberWithSuffix(quantity, 0)} units
        </Badge>
      )}
      {cost && node.field !== "totalCost" && (
        <Badge variant="neutral" size="sm">
          {formatNumberWithSuffix(cost, 0)} {currency}
        </Badge>
      )}
    </div>
  )
}

function latestDepartureDate(orders: IncomingOrderDetail[]): Date | null {
  return orders.reduce<Date | null>((latest, { expectedDepartureDate: date }) => {
    if (!date) return latest
    return !latest || date > latest ? date : latest
  }, null)
}

// Narrow contract so callers can't silently pass thin `IncomingOrder[]` where detail is required.
export type IncomingOrdersTableDataSource = Partial<
  Pick<IVariantSummary, "variantId" | "variantName" | "productName">
> & {
  incomingOrders: IncomingOrderDetail[]
}

interface IncomingOrderProps {
  columnDef: ColDef<IncomingOrderRow>[]
  filters?: {
    statuses?: IncomingOrderStatus[]
  }
  aggregations?: AggregationMethod[]
  dataSource: IncomingOrdersTableDataSource[]
  showPagination?: boolean
  autoHeight?: boolean
  searchText?: string
  id?: string
  loading?: boolean
  fitGridWidth?: boolean
  skuId?: string
}

export default function IncomingOrdersTable({
  columnDef,
  filters,
  aggregations,
  dataSource,
  showPagination = true,
  autoHeight = false,
  searchText,
  id = "incoming-orders-overview",
  loading = false,
  fitGridWidth = false,
  skuId,
}: IncomingOrderProps) {
  const gridRef = useRef<AgGridReact<IncomingOrderRow>>(null)

  const { data: warehouses } = api.warehouse.get.useQuery()
  const { data: company } = api.company.getCompany.useQuery()
  const { filteredCollections, filteredVendorIds } = useFilters()

  const [openDrawer, setOpenDrawer] = useState(false)
  const [selectedIncomingOrders, setSelectedIncomingOrders] = useState<IncomingOrderDetail[]>([])

  // Filter variants based on selected statuses
  const filteredVariants = useMemo(() => {
    return dataSource.map((variant) => {
      const filteredIncomingOrders = filterIncomingOrders(variant.incomingOrders || [], {
        statuses: filters?.statuses ?? [
          IncomingOrderStatus.Arrived,
          IncomingOrderStatus.Delayed,
          IncomingOrderStatus.OnTime,
        ],
        externalCollectionIds: filteredCollections
          .map((c) => c.externalId)
          .filter((id): id is string => id !== null),
        vendorIds: filteredVendorIds.length > 0 ? filteredVendorIds : null,
      })

      return {
        ...variant,
        incomingOrders: filteredIncomingOrders,
      }
    })
  }, [dataSource, filters, filteredCollections, filteredVendorIds])

  const incomingOrders = useMemo(() => {
    const all = filteredVariants.flatMap((variant) => variant.incomingOrders)
    if (!skuId) return all
    return all.filter((order) => skuQuantityInOrder(order, skuId) > 0)
  }, [filteredVariants, skuId])

  // Handle cell clicks
  const handleCellClicked = useCallback((params: CellClickedEvent<IncomingOrderRow>) => {
    const entries = filterDefined(
      params.node.group ? params.node.allLeafChildren?.map((node) => node.data) : [params.data],
    )

    if (!entries) return

    // For now, just log the click - can be extended later for navigation
    console.info("Cell clicked:", { params })
    setSelectedIncomingOrders(
      entries
        .flatMap((entry) => entry.rawIncomingOrders)
        .filter((order) => order.status === params.data?.status),
    )
    setOpenDrawer(true)
  }, [])

  // Process incoming orders - one row per delivery (order + status + warehouse + ETA).
  // A production order can arrive in several shipments, each with its own ETA; collapsing them
  // to one row per order forced a single arbitrary ETA onto the whole order (HAK-2476).
  const processedIncomingOrders = useMemo(() => {
    const grouped = groupBy(incomingOrders, (order) =>
      [
        order.productionOrderId,
        order.status,
        order.warehouseId,
        new Date(order.expectedArrivalDate).getTime(),
      ].join("|"),
    )
    // Same display logic as VariantCellRenderer in ProductCellRenderers.tsx
    const variantIdToName = new Map(
      filteredVariants.map((v) => {
        const variantName = v.variantName ?? "Unknown variant"
        const productName = v.productName ?? "Unknown product"
        const showBothNames =
          !variantName.includes(productName) && productName !== "Unknown product"
        return [v.variantId, showBothNames ? `${productName} / ${variantName}` : variantName]
      }),
    )

    return Object.entries(grouped)
      .map(([deliveryKey, deliveryOrders]): IncomingOrderRow | null => {
        const firstOrder = deliveryOrders[0]
        if (!firstOrder) return null

        const variantIds = uniq(deliveryOrders.map((order) => order.variantId))
        const quantity = skuId
          ? sumBy(deliveryOrders, (order) => skuQuantityInOrder(order, skuId))
          : sumBy(deliveryOrders, "quantity")
        const cost = skuId
          ? sumBy(deliveryOrders, (order) => skuCostShareInOrder(order, skuId))
          : sumBy(deliveryOrders, "cost")

        // A delivery that carries no units is noise the order-level grouping used to hide.
        if (quantity === 0) return null

        // Only use externalCollectionId if all orders have the same value
        const collectionIds = uniq(deliveryOrders.map((o) => o.externalCollectionId))
        const externalCollectionId = collectionIds.length === 1 ? collectionIds[0] : undefined

        // Only use vendorId if all orders have the same value
        const vendorIdsUnique = uniq(deliveryOrders.map((o) => o.vendorId))
        const vendorId = vendorIdsUnique.length === 1 ? vendorIdsUnique[0] : undefined

        const warehouse = warehouses?.find((w) => w.id === firstOrder.warehouseId)
        return {
          id: firstOrder.productionOrderId,
          productionOrderStatusAggKey: deliveryKey,
          productionOrderId: firstOrder.productionOrderId || "Unknown",
          variantCount: variantIds.length,
          quantity: quantity,
          cost: cost,
          warehouseId: firstOrder.warehouseId,
          expectedArrivalDate: firstOrder.expectedArrivalDate,
          expectedDepartureDate: latestDepartureDate(deliveryOrders),
          orderDate: firstOrder.orderDate,
          shipmentMethod: firstOrder.shipmentMethod,
          externalCollectionId,
          vendorId: vendorId ?? undefined,
          extraData: firstOrder.extraData,
          variantIds: variantIds,
          variantNames: variantIds.map((id) => variantIdToName.get(id) ?? id),
          status: firstOrder.status || "Unknown",
          source: firstOrder.source,
          // Additional fields for aggregation
          warehouseName: warehouse?.name || firstOrder.warehouseId,
          rawIncomingOrders: deliveryOrders,
        }
      })
      .filter((item): item is IncomingOrderRow => item !== null)
  }, [incomingOrders, warehouses, filteredVariants, skuId])

  const displayedOrders = useMemo(() => {
    if (!searchText) return processedIncomingOrders
    return filterOnSearchString(processedIncomingOrders, searchText)
  }, [processedIncomingOrders, searchText])

  // Auto group column definition
  const autoGroupColumnDef: ColDef = useMemo(() => {
    return {
      field: "productionOrderId",
      onCellClicked: handleCellClicked,
      headerName: startCase(
        aggregations?.[0]?.replace("(extradata)", "").replace("(incoming-order-extradata)", "") ??
          "",
      ),
      colSpan: (params) => {
        if (params.node?.group) {
          return 20
        }
        return 1
      },
      cellRendererSelector: () => {
        return {
          component: "agGroupCellRenderer",
          params: {
            suppressCount: true,
            innerRenderer: (params: any) => {
              const footer = params.node.footer
              if (footer) {
                return <span className="font-semibold">Totals</span>
              }

              return <GroupCellRenderer {...params} currency={company?.currency ?? "DKK"} />
            },
          },
        }
      },
    }
  }, [aggregations, handleCellClicked, company])

  const defaultColumnDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      tooltipComponent: CustomAgGridTooltip,
      suppressHeaderMenuButton: true,
      onCellClicked: handleCellClicked,
      cellStyle: {
        cursor: "pointer",
      },
    }),
    [],
  )

  // Default sort on expected arrival date
  const initialSortColumn = useMemo(
    () => ({
      name: "expectedArrivalDate",
      direction: "asc" as const,
    }),
    [],
  )

  const gridOptions: GridOptions<IncomingOrderRow> = {
    getRowStyle: (params) => {
      // Highlight delayed incoming orders
      if (
        params.data?.status === "incoming" &&
        params.data?.expectedArrivalDate &&
        isPast(params.data.expectedArrivalDate)
      ) {
        return {
          backgroundColor: getTailwindValue("--color-background-error-highlight"),
        }
      }
    },
    ...(fitGridWidth && { autoSizeStrategy: { type: "fitGridWidth", defaultMinWidth: 80 } }),
  }

  return (
    <div className="flex flex-col h-full">
      <div className="h-full w-full flex flex-col gap-2">
        <div className="flex-1">
          <DataTableBaseV2<IncomingOrderRow>
            id={id}
            gridRef={gridRef}
            rowData={displayedOrders}
            columnDefs={columnDef}
            defaultColumnDef={defaultColumnDef}
            autoGroupColumnDef={autoGroupColumnDef}
            initialSortColumn={initialSortColumn}
            rowIdField="productionOrderStatusAggKey"
            emptyMessage="No incoming orders found"
            groupHideParentOfSingleChild={false}
            loading={loading}
            gridOptions={gridOptions}
            showPagination={showPagination}
            autoHeight={autoHeight}
          />
        </div>
      </div>
      <IncomingOrdersDrawer
        trigger={<></>}
        incomingOrders={selectedIncomingOrders}
        customOpen={openDrawer}
        onClose={() => {
          setOpenDrawer(false)
        }}
      />
    </div>
  )
}
